'use client'

import { useAddProductToFavoriteList } from '@/feature/product/hooks/useAddProductToFavoriteList'
import { handleErrors } from '@/lib/graphql/apiErrors'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Drawer, DrawerAction, DrawerBody, DrawerFooter } from '@/ui/drawer/drawer/Drawer.client'
import { useForm, useWatch } from '@/ui/form/Form.client'
import { Select } from '@/ui/form/inputs/select/Select.client'
import { useToast } from '@/ui/toast/Toast'
import { useApolloClient, useLazyQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import {
    type AddProductToFavoritesList_ListFragment,
    Direction,
    GetUserFavoriteListsDocument,
} from '@gql-generated/graphql'
import { useAtom } from 'jotai'
import { useCallback, useEffect, useMemo } from 'react'
import {
    createFavoriteListAddProductDrawerCloseIntent,
    createFavoriteListProductAddedFailedIntent,
    createFavoriteListProductAddedIntent,
} from '../../services/favorite.intents'
import { FavoritesListNameInput } from '../upsert-favorites-list-drawer/_components/favorites-list-name-input/FavoritesListNameInput.client'
import { productToAddFavoriteListDrawerAtom } from './addProductToFavorite.store'

gql(/* GraphQL */ `
  fragment AddProductToFavoritesList_List on Wishlist {
    id
    name
  }

  query getUserFavoriteLists($input: Browsing!) {
    currentUser {
      id
      wishlists(input: $input) {
        items {
          ...AddProductToFavoritesList_List
        }
      }
    }
  }
`)

function AddToExistingListContent({
  lists,
  isCreation,
}: {
  lists: AddProductToFavoritesList_ListFragment[]
  isCreation: boolean
}) {
  const { t } = useTranslation(['addProductToFavoriteListDrawer'])

  const items = useMemo(
    () =>
      lists.map((list) => ({
        id: list.id.toString(),
        label: list.name || '',
      })),
    [lists],
  )

  return (
    <div className="flex flex-col gap-5">
      <Select
        name="listId"
        label={t('addProductToFavoriteListDrawer:select.label')}
        items={items}
        placeholder={t('addProductToFavoriteListDrawer:select.placeholder')}
        required={!isCreation}
        maxHeight={360}
      />
      <div className="flex flex-col gap-2">
        <h2 className="text-3xl font-bold">
          {t('addProductToFavoriteListDrawer:title.hasFavorites.false')}
        </h2>
        <FavoritesListNameInput />
      </div>
    </div>
  )
}

/**
 * Opened from product page or product card (Heart icon)
 */
export function AddProductToFavoriteListDrawer() {
  const { t } = useTranslation(['addProductToFavoriteListDrawer'])
  const client = useApolloClient()

  const [favoriteDrawerProduct, setFavoriteDrawerProduct] = useAtom(
    productToAddFavoriteListDrawerAtom,
  )

  const productSku = favoriteDrawerProduct?.productSku
  const productName = favoriteDrawerProduct?.productName
  const trackingEventCategory = favoriteDrawerProduct?.trackingEventCategory || ''

  const closeDrawer = useCallback(() => setFavoriteDrawerProduct(null), [setFavoriteDrawerProduct])

  const [getFavoriteLists, { loading, data }] = useLazyQuery(GetUserFavoriteListsDocument)
  const toast = useToast()

  const favoritesList = useMemo(() => data?.currentUser?.wishlists?.items || [], [data])
  const hasFavoritesList = favoritesList.length > 0

  const form = useForm({
    defaultValues: {
      name: '',
      listId: '',
    },
  })

  const { reset: resetForm } = form

  const [name, listId] = useWatch({
    control: form.control,
    name: ['name', 'listId'],
  })

  const isCreation = !!name

  const updateOrCreateFavoriteList = useAddProductToFavoriteList({
    name,
    favoriteListId: Number(listId),
    productRef: productSku,
  })

  const handleClose = useCallback(async () => {
    resetForm() // Reset the form when closing the drawer
    closeDrawer()
  }, [closeDrawer, resetForm])

  useEffect(() => {
    if (productSku) {
      getFavoriteLists({
        variables: {
          input: {
            sort: {
              field: 'createdAt',
              direction: Direction.Desc,
            },
          },
        },
      })
    }
  }, [getFavoriteLists, productSku])

  useEffect(() => {
    if (name) {
      form.resetField('listId') // Reset listId when name is set
    }
  }, [name, form])

  useEffect(() => {
    if (listId) {
      form.resetField('name') // Reset name when listId is set
    }
  }, [listId, form])

  useEffect(() => {
    if (hasFavoritesList && !!productSku) {
      form.setValue('listId', favoritesList[0]?.id.toString() ?? '')
    }
  }, [hasFavoritesList, form, favoritesList, productSku])

  const handleAddProductToWishlist = useCallback(async () => {
    const listName =
      form.getValues().name ||
      favoritesList.find((list) => list.id.toString() === form.getValues().listId)?.name

    const { errors } = await updateOrCreateFavoriteList()

    handleErrors(errors, {
      onError: {
        default: () => {
          toast.error({
            message: t('addProductToFavoriteListDrawer:toast.error', {
              productName,
              listName,
            }),
          })
          handleIntent(createFavoriteListProductAddedFailedIntent(), {})
        },
      },
      onSuccess: () => {
        if (isCreation) {
          pushToGtmDataLayer({
            event: 'gaEvent',
            eventCategory: trackingEventCategory,
            eventAction: 'wishlist',
            eventLabel: 'confirmation création wishlist',
          })
        } else {
          pushToGtmDataLayer({
            event: 'gaEvent',
            eventCategory: trackingEventCategory,
            eventAction: 'wishlist',
            eventLabel: 'confirmation ajout wishlist',
          })
        }

        toast.success({
          dedupe: false,
          message: t('addProductToFavoriteListDrawer:toast.success', {
            productName,
            listName,
          }),
        })

        handleIntent(createFavoriteListProductAddedIntent(), {
          whenInBrowser() {
            client.refetchQueries({
              include: [GetUserFavoriteListsDocument],
            })
            handleClose()
          },
        })
      },
    })
  }, [
    form,
    favoritesList,
    updateOrCreateFavoriteList,
    toast,
    t,
    productName,
    isCreation,
    handleClose,
    client,
    trackingEventCategory,
  ])

  const cancelButtonIntent = useMemo(() => createFavoriteListAddProductDrawerCloseIntent(), [])

  return (
    <Drawer
      isOpen={!!productSku}
      onOpenChange={closeDrawer}
      isLoading={loading}
      title={t(`addProductToFavoriteListDrawer:title.hasFavorites.${hasFavoritesList}`)}
    >
      <DrawerBody>
        {hasFavoritesList ? (
          <AddToExistingListContent lists={favoritesList} isCreation={isCreation} />
        ) : (
          <FavoritesListNameInput required />
        )}
      </DrawerBody>
      <DrawerFooter>
        <DrawerAction type="cancel" intent={cancelButtonIntent}>
          {t('addProductToFavoriteListDrawer:cta.cancel')}
        </DrawerAction>
        <DrawerAction
          type="submit"
          form={form}
          action={handleAddProductToWishlist}
          ariaLabel={t('addProductToFavoriteListDrawer:cta.description')}
          intent={INTENT_SAME_AS_WEB}
        >
          {t('addProductToFavoriteListDrawer:cta.submit')}
        </DrawerAction>
      </DrawerFooter>
    </Drawer>
  )
}
